import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiBlob } from "../../api";
import { cleanBodyFolded } from "../timelineModel.mjs";
import { shortDate, clock, plural } from "./format.mjs";
import { meetingNotePost } from "./meetingNotes.mjs";
import { actionItemsOf } from "./lead/leadModel.mjs";
import AddToRoomMenu from "../rooms/AddToRoomMenu";

/*
 * ONE TIMELINE ROW, in the frames' grammar (1a, 1b, 1c, 1d, 1g): the clock, a
 * marker (a ring for a message, a square for a milestone, a diamond for a form,
 * an outlined square for a call or meeting, a grey dot for a PandaDoc notice),
 * who and the first line ("You → Shahar, Gali: All set on our side…"), and the
 * grey sub-line (the subject once per thread per day, the pricing quote, the Cc,
 * "snippet only", a call's to-dos, "· 2 calendar emails folded").
 *
 * A past meeting nobody has answered carries its controls ON the row, as drawn:
 * "Not recorded  Held  No-show  Add notes". Held and No-show write POST
 * /calendar/outcome (ops.meetingOutcome); Add notes opens a note under the row
 * and saves it as a call note carrying the meeting's event id, which the tab
 * then reads under this meeting (record/timelineView.mjs foldCalls).
 *
 * EVERY ROW EXPANDS IN PLACE, several at once, with Collapse all in the header
 * (RecordTimeline owns which are open). The head line is a real button, so the
 * keyboard reaches every row; a click anywhere on the row does the same, except on
 * a link or a control inside it.
 *   stage move  its evidence box: what it was read from, whether it needed your
 *               approval, or that it was set by hand and the last message before
 *               it (reality.txt [7]); never "confidence high"
 *   email       the body with the signature folded, who sent it to whom (or
 *               "Direction not recorded"), the Cc, Open in Gmail ↗
 *   call        length and who was on it, the summary, its action items (read from
 *               the transcript; Fireflies' to-dos until then), the calendar entry
 *               it folded, the transcript (opens in the CRM, ops.openTranscript)
 *   meeting     who was invited and how they answered, its status, the notes,
 *               the join link while it is ahead
 *   form        every answer, through formAnswers.mjs (the view's `answers`)
 *   DM          the message and its stored screenshot
 *   document    who opened what, from PandaDoc's notification email, Open in Gmail ↗
 *
 * Props:
 *   row       a row from record/timelineView.mjs timelineView (a buildTimeline
 *             rowView plus what the tab adds: noteTarget, meeting facts, gmail)
 *   open      boolean
 *   onToggle  () => void
 *   ops       ops.meetingOutcome(eventKey, "held" | "no_show" | null),
 *             ops.call (the meeting note), ops.reload, ops.openTranscript
 */

const when = (ts) => (ts ? `${shortDate(ts)} · ${clock(ts)}` : "");

/** Does the row hold anything more than its line? */
export function canExpand(row) {
  const it = row.item || {};
  switch (row.kind) {
    case "stage":
      return (row.evidenceLines || []).length > 0;
    case "email":
    case "dm":
      return true;
    case "note":
    case "task":
      return !!String(it.body || "").trim();
    case "form":
      return (row.answers || []).length > 0 || !!row.message;
    case "call":
    case "meeting":
    case "document":
      return true;
    default:
      return !!String(it.body || "").trim();
  }
}

// The stored screenshot behind a DM, or an email's first attachment. The
// attachment route is auth-gated, so the bytes come with the token and show from
// an object URL; a click opens that original in a new tab.
function Attachment({ attachment }) {
  const [url, setUrl] = useState(null);
  const image = /^image\//.test(String(attachment.mime_type || ""));
  useEffect(() => {
    let objectUrl = null;
    let live = true;
    apiBlob(`/attachments/${attachment.id}`)
      .then((blob) => {
        if (!live) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {});
    return () => {
      live = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.id]);
  const name = attachment.filename || (image ? "screenshot" : "attachment");
  return (
    <button type="button" className="rec-tl__attach" disabled={!url} onClick={() => window.open(url, "_blank", "noopener")} title="Open the stored file">
      {image && url ? <img src={url} alt={name} /> : <span className="rec-tl__attachicon" aria-hidden="true">▤</span>}
      <span>{name} ↗</span>
    </button>
  );
}

// The body as written, the signature folded behind one click (a wrong detection
// costs a click; deleting real content costs trust in everything else here).
function Body({ text }) {
  const [sig, setSig] = useState(false);
  const { body, signature } = cleanBodyFolded(text || "");
  return (
    <>
      {body ? <div className="rec-tl__text">{body}</div> : <div className="rec-tl__text is-empty">No text was stored with this entry.</div>}
      {signature && (
        <>
          <button type="button" className="rec-link rec-link--quiet rec-tl__sigtoggle" onClick={() => setSig((v) => !v)}>
            {sig ? "Hide signature" : "· signature"}
          </button>
          {sig && <div className="rec-tl__text rec-tl__sig">{signature}</div>}
        </>
      )}
    </>
  );
}

function GmailLink({ url }) {
  return url ? (
    <a className="rec-tl__out" href={url} target="_blank" rel="noopener noreferrer">Open in Gmail ↗</a>
  ) : null;
}

function Expanded({ row, ops }) {
  const [error, setError] = useState(null);
  const it = row.item || {};
  const meta = it.metadata || {};

  if (row.kind === "stage") {
    return (
      <div className="rec-evidence">
        {(row.evidenceLines || []).map((l) => (
          <span key={l}>{l}</span>
        ))}
      </div>
    );
  }

  if (row.kind === "email") {
    return (
      <div className="rec-evidence rec-tl__open">
        <Body text={it.body} />
        <span className="rec-tl__facts">
          {[row.direction_line, when(it.occurred_at), row.ccNames && row.ccNames.length ? `Cc ${row.ccNames.join(", ")}` : null].filter(Boolean).join(" · ")}
        </span>
        {row.snippet && <span className="rec-tl__facts">Snippet only: the full message was not stored.</span>}
        {meta.bulk && <span className="rec-tl__facts">Bulk mail: logged, and no automation ran on it.</span>}
        {row.attachment && <Attachment attachment={row.attachment} />}
        <span className="rec-tl__roomrow">
          <GmailLink url={row.gmail} />
          {/* Onto the company's prospect room, as written or as a call recap (src/rooms). */}
          {!meta.bulk && <AddToRoomMenu row={it} company={ops && ops.roomCompany} />}
        </span>
      </div>
    );
  }

  if (row.kind === "dm") {
    return (
      <div className="rec-evidence rec-tl__open">
        <Body text={it.body} />
        <span className="rec-tl__facts">{[it.subtype === "whatsapp_message" ? "WhatsApp" : "LinkedIn", row.direction_line, when(it.occurred_at)].filter(Boolean).join(" · ")}</span>
        {row.attachment ? <Attachment attachment={row.attachment} /> : <span className="rec-tl__facts">No screenshot stored.</span>}
      </div>
    );
  }

  if (row.kind === "call") {
    const c = row.call;
    const m = c.meeting;
    const actions = actionItemsOf(it.commitments);
    return (
      <div className="rec-evidence rec-tl__open">
        <span className="rec-tl__facts">
          {[c.minutes ? `${c.minutes} min` : null, it.source === "fireflies" ? "recorded by Fireflies" : "notes typed in", c.attendees.length ? `with ${c.attendees.join(", ")}` : null].filter(Boolean).join(" · ")}
        </span>
        {c.points.length > 0 ? (
          <ul className="rec-tl__points">
            {c.points.map((p, i) => (
              <li key={i}>{p.label ? <b>{p.label}: </b> : null}{p.text}</li>
            ))}
          </ul>
        ) : (
          it.body && <Body text={it.body} />
        )}
        {actions.groups.map((g) => (
          <span key={g.label}><b>{g.label}:</b> {g.items.map((x) => x.text).join(" · ")}</span>
        ))}
        {actions.state === "read" && !actions.groups.length && <span className="rec-tl__facts">Nothing promised, quoted or agreed on the call.</span>}
        {actions.state === "thin" && <span className="rec-tl__facts">Fireflies caught only {actions.coverage.words} words of this call, so no action items could be read.</span>}
        {actions.state === "unread" && c.todos.ours.length > 0 && <span><b>Ours:</b> {c.todos.ours.join(" · ")}</span>}
        {actions.state === "unread" && c.todos.theirs.length > 0 && <span><b>Theirs:</b> {c.todos.theirs.join(" · ")}</span>}
        {m && <span className="rec-tl__facts">On the calendar: {[it.title, m.range, m.status].filter(Boolean).join(" · ")}</span>}
        {it.source === "fireflies" && it.external_id && (
          <Link className="rec-tl__out" to={`/meetings/calls/${encodeURIComponent(it.external_id)}`}>Open call: notes and transcript</Link>
        )}
        {it.source === "fireflies" && ops.openTranscript ? (
          <button type="button" className="rec-link rec-tl__out" onClick={() => ops.openTranscript(it.id)}>Transcript</button>
        ) : (
          c.transcript_url && <a className="rec-tl__out" href={c.transcript_url} target="_blank" rel="noopener noreferrer">Transcript ↗</a>
        )}
      </div>
    );
  }

  if (row.kind === "meeting") {
    const m = row.meeting;
    const clear = () => {
      setError(null);
      Promise.resolve(ops.meetingOutcome(m.event_key, null)).catch((e) => setError(e.message));
    };
    return (
      <div className="rec-evidence rec-tl__open">
        <span>{m.attendees.length ? `Invited: ${m.attendees.map((a) => (a.response ? `${a.name} (${a.response})` : a.name)).join(", ")}` : "No attendees recorded"}</span>
        <span className="rec-tl__facts">{[m.range, m.status, m.organizer ? `organised by ${m.organizer}` : null].filter(Boolean).join(" · ")}</span>
        {(row.notes || []).map((n) => (
          <span key={n.id} className="rec-tl__note"><b>Notes:</b> {n.body}</span>
        ))}
        {!m.past && m.join && (
          <a className="rec-tl__out" href={m.join} target="_blank" rel="noopener noreferrer">Join link ↗</a>
        )}
        {m.outcome && m.event_key && (
          <span className="rec-inline">
            <span className="rec-tl__facts">Recorded as {m.outcome === "held" ? "held" : "a no-show"}</span>
            <button type="button" className="rec-link rec-link--quiet" onClick={clear}>Clear</button>
          </span>
        )}
        {error && <span className="rec-err">{error}</span>}
      </div>
    );
  }

  if (row.kind === "form") {
    return (
      <div className="rec-evidence rec-tl__open">
        {row.message && <div className="rec-tl__text">{row.message}</div>}
        {row.answers && row.answers.length > 0 && (
          <div className="rec-kv rec-kv--150 rec-tl__answers">
            {row.answers.map((a) => (
              <React.Fragment key={a.key}>
                <span>{a.label}</span>
                <span>{a.value}</span>
              </React.Fragment>
            ))}
          </div>
        )}
        <span className="rec-tl__facts">{[it.title || "Website form", when(it.occurred_at), meta.landing_page || null].filter(Boolean).join(" · ")}</span>
      </div>
    );
  }

  if (row.kind === "document") {
    const d = row.document;
    return (
      <div className="rec-evidence rec-tl__open">
        <span>
          {[d.viewer ? `${d.viewer}${d.viewer_email ? ` (${d.viewer_email})` : ""}` : "Someone", d.event === "signed" ? "signed" : "opened", d.name ? `“${d.name}”` : "the document"].join(" ")}
        </span>
        <span className="rec-tl__facts">Read from PandaDoc’s notification email · {when(it.occurred_at)}</span>
        <GmailLink url={row.gmail} />
      </div>
    );
  }

  return it.body ? (
    <div className="rec-evidence rec-tl__open">
      <Body text={it.body} />
      <span className="rec-tl__facts">{when(it.occurred_at)}</span>
    </div>
  ) : null;
}

// "Add notes" under a meeting: what was said, saved as a note on the meeting's
// own key, so it reads under this meeting (and in the Call held block) from then on.
function MeetingNote({ row, ops, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const t = row.noteTarget;
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      // The one shape for notes on a meeting (record/meetingNotes.mjs), the same
      // the Call held block writes, so each reads under the meeting in the other.
      const post = meetingNotePost({ meeting: t, body: text, contactId: t.contact_id, companyId: t.company_id });
      if (!post) throw new Error("Nothing to save yet.");
      await ops.call("/activities", { method: "POST", body: JSON.stringify(post) });
      await ops.reload();
      onDone();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };
  return (
    <div className="rec-tl__compose">
      <textarea className="rec-textarea" autoFocus placeholder={`What was said on ${t.title}`} value={text} onChange={(e) => setText(e.target.value)} />
      <div className="rec-tl__composeactions">
        {error && <span className="rec-err">{error}</span>}
        <button type="button" className="rec-btn rec-btn--plain rec-btn--sm" onClick={onDone}>Cancel</button>
        <button type="button" className="rec-btn rec-btn--dark rec-btn--sm" disabled={!text.trim() || busy} onClick={save}>{busy ? "Saving…" : "Save notes"}</button>
      </div>
    </div>
  );
}

// The sub-line of a meeting: its state, the controls a past unanswered one needs,
// then what folded under it.
function MeetingSub({ row, ops, onNote }) {
  const [error, setError] = useState(null);
  const m = row.meeting;
  const answer = (outcome) => {
    setError(null);
    Promise.resolve(ops.meetingOutcome(m.event_key, outcome)).catch((e) => setError(e.message));
  };
  const unanswered = m.past && !m.outcome && !m.cancelled && !!m.event_key;
  return (
    <div className="rec-tl__sub rec-tl__sub--controls">
      <span>{m.status}</span>
      {m.their_time && <span>{m.their_time}</span>}
      {unanswered && (
        <>
          <button type="button" className="rec-link" onClick={() => answer("held")}>Held</button>
          <button type="button" className="rec-link" onClick={() => answer("no_show")}>No-show</button>
        </>
      )}
      {m.past && !m.cancelled && <button type="button" className="rec-link" onClick={onNote}>{row.notes && row.notes.length ? "Add more notes" : "Add notes"}</button>}
      {row.notes && row.notes.length > 0 && <span>{plural(row.notes.length, "note")}</span>}
      {row.folded && <span className="rec-tl__folded">· {row.folded}</span>}
      {error && <span className="rec-err">{error}</span>}
    </div>
  );
}

export default function TimelineRow({ row, open = false, onToggle = null, ops }) {
  const [noting, setNoting] = useState(false);
  const expandable = canExpand(row);
  const toggle = () => expandable && onToggle && onToggle();
  const colon = row.who && row.text && row.sep === ": ";

  const line = row.milestone ? (
    <span className="rec-tl__milestone">{row.text}</span>
  ) : (
    <span className="rec-tl__line">
      {row.who && <b className="rec-tl__who">{row.who}{colon ? ":" : ""}</b>}
      {row.who && row.text ? (colon ? " " : " · ") : ""}
      {row.text}
    </span>
  );

  return (
    <div
      className={`rec-tl__row${expandable ? " is-expandable" : ""}${open ? " is-open" : ""}`}
      onClick={(e) => {
        // A control or a link inside the row does its own thing; anywhere else on
        // the row opens it, the way the head line's button does.
        if (e.target.closest("a, button, input, textarea, select")) return;
        toggle();
      }}
    >
      <span className="rec-tl__time">{row.clock}</span>
      <span className={`rec-mark rec-mark--${row.marker}`} aria-hidden="true" />
      <div className="rec-tl__content">
        {expandable ? (
          <button type="button" className="rec-tl__head" aria-expanded={open} onClick={toggle}>
            {line}
          </button>
        ) : (
          <div className="rec-tl__head">{line}</div>
        )}
        {row.kind === "meeting" ? (
          <MeetingSub row={row} ops={ops} onNote={() => setNoting(true)} />
        ) : (
          row.sub && row.sub.length > 0 && <div className={`rec-tl__sub${row.kind === "call" ? " rec-tl__sub--call" : ""}`}>{row.sub.join(" · ")}</div>
        )}
        {open && expandable && <Expanded row={row} ops={ops} />}
        {noting && <MeetingNote row={row} ops={ops} onDone={() => setNoting(false)} />}
      </div>
    </div>
  );
}
